import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, useLocation } from 'react-router-dom';
import { Formik, FormikValues, yupToFormErrors } from 'formik';
import { set, isEqual, get, update, cloneDeep, upperFirst } from 'lodash';
import { KeyboardReactInterface } from 'react-simple-keyboard';

import history from 'browserHistory';
import { useModal } from 'modules/shared/hooks';
import { useValidationSchema } from 'modules/common/CommonTrip/hooks/useValidationSchema';
import { branchTitleSelector } from 'modules/common/LogoutBtn/selectors';
import KioskHeader from 'modules/kiosk/components/KioskHeader';
import Loader from 'modules/kiosk/components/Loader';
import Landing from 'modules/kiosk/containers/Landing';
import { faceIDSelector } from 'modules/security/containers/TripContainer/services/selectors';
import ErrorMsg from 'modules/shared/ErrorMsg';
import AddingVisitor from 'modules/kiosk/containers/AddingVisitor';
import { commonTripSelector, dataDocsSelector, visitorSelector } from 'modules/common/CommonTrip/selectors';
import { INITIAL_VALUE, usePrepareValues } from 'modules/common/CommonTrip/hooks/usePrepareValues';
import {
  cleanError,
  getNewTripSuccess,
  getTripRequest,
  setVisitorName,
  updateTripRequest,
} from 'modules/common/CommonTrip/actions';
import ModalAcept from 'modules/kiosk/components/ModalAcept';
import { getTripFieldsSuccess, tripLoadingSelector } from 'modules/common/FillTrip';
import FormikFillFields from 'modules/shared/Forms/FormikFillFields';
import CustomKeyboard from 'modules/kiosk/components/CustomKeyboard';
import { objToFormData } from 'utils';
import { RootState } from 'rootReducer';
import { Trip, TripService, tripDetailsType, tripStatuses } from '../../../../entities/Trip';
import { changeActiveInput } from './services/actions';
import { kioskSelector } from './services/selectors';
import CreatedTripInfo from '../CreatedTripInfo';
import { pathService } from './services/path.service';
import '../../custom.scss';

const Kiosk: React.FC = () => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const keyboardRef = useRef<KeyboardReactInterface | null>(null);

  const { isShow, openModal, closeModal } = useModal();
  const { isShow: isShowSubmit, openModal: openModalSubmit, closeModal: closeModalSubmit } = useModal();
  const tripId = history.location.pathname.split('/')[3] || '';

  const [tripDataReq, setTripDataReq] = useState<any>();
  // const [isVisibleBtn, setIsVisibleBtn] = useState(true);

  const { loadingTrip, errorTrip, trip } = useSelector(commonTripSelector);
  const { status } = trip;
  const statusId = status?.id || 1;
  const { loadFaceID } = useSelector(faceIDSelector);
  const dataDocs = useSelector(dataDocsSelector);
  const dataVisitor = useSelector(visitorSelector);
  const loadData = useSelector(tripLoadingSelector);

  const { activeInput } = useSelector(kioskSelector);
  const role = useSelector((state: RootState) => state.authModuleReducer.authReducer.role);

  const { id } = trip;
  const initValues = usePrepareValues(id, trip);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isTripCreation, setIsTripCreation] = useState<boolean>(false);

  const operatingProcedure = useMemo(() => {
    if (role?.branches && role.branches.length !== 0) {
      return role.branches.find((branch) => branch?.id === JSON.parse(localStorage.getItem('branchId') || 'null')?.id)
        ?.operatingProcedure;
    }

    return null;
  }, [role]);

  const branchTitle = useSelector(branchTitleSelector);
  const useVisitorDoc: boolean = branchTitle?.useVisitorDoc ?? true;
  const useTransportDoc: boolean = branchTitle?.useTransportDoc ?? true;
  const validationSchema = useValidationSchema(
    'security',
    1,
    false,
    operatingProcedure ?? '',
    useVisitorDoc,
    useTransportDoc,
  );

  const closeModalHandler = () => {
    const timer = setTimeout(() => {
      closeModalSubmit();

      return history.push('/kiosk');
    }, 3000);

    clearTimeout(timer);
    closeModal();

    return history.push('/kiosk');
  };

  const handleOnSubmit = () => {
    closeModalSubmit();
    dispatch(updateTripRequest(tripDataReq));
    openModal();

    return closeModalHandler();
  };

  const onOpenKeyboard = (e: any) => {
    e.stopPropagation();
    dispatch(changeActiveInput(e.target.name));
  };

  const onDeleteField = (type: tripDetailsType) => {
    const initValue: Trip = cloneDeep(get(INITIAL_VALUE, type));

    // @ts-ignore
    const tripValue = cloneDeep(formRef.current?.values);
    if (type === 'driver' || type === 'forwarder') set(tripValue, `is${upperFirst(type)}PhoneNumberValid`, false);
    const docsDatasNew = dataDocs.filter((d: any) => !isEqual(d?.fieldName, type));
    const updated = update(tripValue, type, () => initValue);
    const loaded = {
      type,
      isError: false,
    };

    const visitors = dataVisitor.filter((f: any) => !isEqual(f.fieldName, type));

    dispatch(setVisitorName([...visitors]));
    dispatch(getNewTripSuccess(updated, docsDatasNew));
    dispatch(getTripFieldsSuccess({ type, data: initValue }, loaded));

    // @ts-ignore
    keyboardRef?.current?.setInput('');
  };

  useEffect(() => {
    if (errorTrip) {
      return openModal();
    }
    closeModal();
  }, [errorTrip]);

  useEffect(() => {
    if (tripId && !id) {
      dispatch(getTripRequest(tripId));
    }

    document.addEventListener('click', () => dispatch(changeActiveInput('')));
  }, []);

  const location = useLocation();

  const handleCreateRaceWithTransportData = async () => {
    setIsTripCreation(true);

    try {
      const intentionId = pathService.getIntention(location.pathname);

      if (!intentionId) {
        setErrorMessage('Сталася помилка при автоматичному створенні рейсу');
        return;
      }

      const branchLocal = localStorage.getItem('branchId') || 'null';
      const branchId = JSON.parse(branchLocal)?.id;

      const newTrip = await TripService.getNewTrip(
        objToFormData({
          branchId,
          tripIntention: intentionId,
        }),
      );

      const updatedTrip = {
        ...newTrip,
        ...trip,
        id: newTrip.id,
        status: newTrip.status,
        statusId: newTrip.status.id,
        tripIntention: intentionId,
        branchId,
      };

      const data = cloneDeep(updatedTrip);
      await TripService.updateTrip(newTrip.id, objToFormData(data));

      dispatch(getTripRequest(newTrip.id.toString()));
      history.push(`/kiosk/tripCreated`);
    } catch (error) {
      setErrorMessage('Сталася помилка при автоматичному створенні рейсу');
      openModal();
    } finally {
      setIsTripCreation(false);
    }
  };

  useEffect(() => {
    if (trip?.vehicle?.licensePlate) {
      handleCreateRaceWithTransportData();
    }
  }, [trip?.vehicle?.licensePlate]);

  const validate = (v: any) => {
    try {
      validationSchema.validateSync(v, { abortEarly: false, context: { values: v } });
      return {};
    } catch (error) {
      return yupToFormErrors<FormikValues>(error);
    }
  };

  return (
    <div className="kiosk-main">
      <Formik
        // @ts-ignore
        innerRef={formRef}
        initialValues={initValues}
        validateOnMount
        enableReinitialize
        validationSchema={validationSchema}
        validate={validate}
        onSubmit={(values) => {
          // eslint-disable-next-line
          delete values.status;
          // @ts-ignore
          let newStatusId = tripStatuses.left;
          if (statusId === tripStatuses.create) {
            newStatusId = String(values.tripIntention) === '1' ? tripStatuses.parkingInLine : tripStatuses.unloading;
          }
          const branchLocal = localStorage.getItem('branchId') || 'null';
          const branchId = JSON.parse(branchLocal)?.id;
          set(values, 'branchId', branchId);
          set(values, 'statusId', newStatusId);
          setTripDataReq(values);
          openModalSubmit();
        }}
      >
        {({ values, setFieldValue, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit} className="w-100 vh-100" autoComplete="off">
            <FormikFillFields />

            <KioskHeader />

            <Switch>
              <Route path="/kiosk" exact component={Landing} />

              <Route
                path="/kiosk/trip/new/:intention"
                exact
                render={() => (
                  <AddingVisitor
                    handleChange={handleChange}
                    onOpenKeyboard={onOpenKeyboard}
                    loading={loadingTrip}
                    onDeleteField={onDeleteField}
                  />
                )}
              />

              <Route path="/kiosk/tripCreated" exact render={() => <CreatedTripInfo />} />
            </Switch>

            {activeInput ? (
              <CustomKeyboard
                keyboardRef={keyboardRef}
                formValues={values}
                setFieldValue={setFieldValue}
                onDeleteField={onDeleteField}
                onClose={() => dispatch(changeActiveInput(''))}
                isKioskPhone
              />
            ) : null}
          </form>
        )}
      </Formik>

      {(loadingTrip || loadFaceID || loadData || isTripCreation) && <Loader />}

      <ErrorMsg
        isShow={isShow}
        error={errorTrip?.message || errorMessage || 'Виникла помилка підключення'}
        closeHandler={() => {
          closeModal();
          dispatch(cleanError());
        }}
      />

      <ModalAcept
        show={isShowSubmit}
        onHide={closeModalSubmit}
        onClickVerify={handleOnSubmit}
        textTitle={`Зареєструвати в'їзд для рейсу ${trip.id}?`}
        textBody=""
        declineBtn="Так"
        acceptBtn="Ні"
      />
    </div>
  );
};

export default Kiosk;
